import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ChatWidget from "../../components/feature/ChatWidget";
import SEOHead from "../../components/feature/SEOHead";
import { teamMembers } from "../../mocks/team";

const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://vidalferrerassessors.es";

const equipoJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Nuestro Equipo de Asesores",
    "description": "Conoce a Pasqual, Anna y Marilo, los asesores especializados de Vidal Ferrer en Pego. Más de 25 años acompañando a autónomos y empresas de la Marina Alta.",
    "url": `${siteUrl}/equipo`,
    "about": {
      "@type": "AccountingService",
      "name": "Vidal Ferrer Asesores"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Pasqual Vidal Ferrer",
    "jobTitle": "Asesor Fiscal y Laboral",
    "worksFor": { "@type": "AccountingService", "name": "Vidal Ferrer Asesores" },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Pego",
      "addressRegion": "Alicante",
      "addressCountry": "ES"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Anna Vidal Ferrer",
    "jobTitle": "Asesora Contable y Financiera",
    "worksFor": { "@type": "AccountingService", "name": "Vidal Ferrer Asesores" },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Pego",
      "addressRegion": "Alicante",
      "addressCountry": "ES"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Marilo Ferrer Bay",
    "jobTitle": "Asesora de Seguros",
    "worksFor": { "@type": "AccountingService", "name": "Vidal Ferrer Asesores" },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Pego",
      "addressRegion": "Alicante",
      "addressCountry": "ES"
    }
  }
];

export default function EquipoPage() {
  const [activeId, setActiveId] = useState<number | null>(null);

  return (
    <>
      <SEOHead
        title="Nuestro Equipo · Asesores en Pego | Vidal Ferrer Asesores"
        description="Conoce a Pasqual, Anna y Marilo, los asesores especializados de Vidal Ferrer en Pego. Más de 25 años acompañando a autónomos y empresas de la Marina Alta."
        keywords="asesores Pego, equipo asesoría fiscal Alicante, Pasqual Vidal Ferrer, asesoría laboral Marina Alta"
        canonical="/equipo"
        jsonLd={equipoJsonLd}
      />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative h-[380px] md:h-[460px] flex items-center overflow-hidden">
          <img
            src="https://storage.readdy-site.link/project_files/db85668d-0973-4f5a-ac63-8b47d247ab38/16c538eb-bff5-42db-8389-59a24ac94f9c_WhatsApp-Image-2026-03-31-at-12.18.56.jpeg?v=2e0c3211eced2e699fd4eec90c3fa381"
            alt="Equipo Vidal Ferrer Asesores"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/55" />
          <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 md:px-10 text-center">
            <p className="text-[#A0D080] text-sm font-semibold uppercase tracking-widest mb-4">
              Nuestro equipo
            </p>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-5 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Los profesionales detrás<br />de tu tranquilidad
            </h1>
            <p className="text-white/75 text-lg max-w-2xl mx-auto">
              Un equipo cercano, con raíces en la Marina Alta y más de 25 años acompañando a autónomos, agricultores y empresas de la comarca.
            </p>
          </div>
        </section>

        {/* Values strip */}
        <section className="bg-[#6B7F5C] py-5">
          <div className="max-w-screen-xl mx-auto px-6 md:px-10">
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
              {[
                { icon: "ri-shield-check-line", label: "Rigor profesional" },
                { icon: "ri-heart-line", label: "Trato cercano" },
                { icon: "ri-map-pin-2-line", label: "Arraigo local" },
                { icon: "ri-refresh-line", label: "Formación continua" },
              ].map((v) => (
                <div key={v.label} className="flex items-center gap-2.5 text-white">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${v.icon} text-base`} />
                  </div>
                  <span className="text-sm font-medium">{v.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team cards */}
        <section className="py-24 bg-[#F8F7F4]">
          <div className="max-w-screen-xl mx-auto px-6 md:px-10">
            <div className="text-center mb-16">
              <p className="text-[#6B7F5C] text-sm font-semibold uppercase tracking-widest mb-3">
                Conoce al equipo
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Asesores con nombre y apellido
              </h2>
              <p className="text-[#6B6B6B] text-base mt-4 max-w-xl mx-auto">
                En Vidal Ferrer Asesores siempre sabes quién lleva tu caso. Sin intermediarios ni call centers.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {teamMembers.map((member) => (
                <div
                  key={member.id}
                  className="bg-white rounded-2xl overflow-hidden group hover:-translate-y-1 transition-transform duration-300"
                >
                  {/* Photo */}
                  <div className="relative h-72 overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-5 right-5">
                      <p className="text-white font-bold text-lg leading-tight" translate="no" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {member.name}
                      </p>
                      <span className="inline-flex items-center mt-1 bg-[#6B7F5C] text-white text-xs font-semibold px-2.5 py-0.5 rounded-full">
                        {member.role}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <p className="text-[#6B6B6B] text-sm leading-relaxed mb-5">
                      {member.description}
                    </p>

                    {/* Specialties */}
                    <div className="mb-5">
                      <p className="text-xs font-semibold uppercase tracking-widest text-[#4A4A4A] mb-2.5">
                        Especialidades
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {member.specialties.map((s) => (
                          <span
                            key={s}
                            translate="no"
                            className="bg-[#F0F4ED] text-[#6B7F5C] text-xs font-medium px-2.5 py-1 rounded-full"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                      <Link
                        to="/contacto"
                        className="flex items-center gap-1.5 text-[#6B7F5C] text-sm font-medium hover:gap-2.5 transition-all cursor-pointer whitespace-nowrap"
                      >
                        Contactar
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line" />
                        </div>
                      </Link>
                      <a
                        href={member.linkedin}
                        rel="nofollow"
                        className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-full text-[#6B6B6B] hover:text-[#6B7F5C] hover:border-[#6B7F5C] transition-colors cursor-pointer"
                      >
                        <i className="ri-linkedin-fill text-sm" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Expandable FAQ about the team */}
        <section className="py-20 bg-white">
          <div className="max-w-screen-xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
              <div>
                <p className="text-[#6B7F5C] text-sm font-semibold uppercase tracking-widest mb-4">Cómo trabajamos</p>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Un asesor dedicado<br />para cada cliente
                </h2>
                <p className="text-[#6B6B6B] text-base leading-relaxed mb-8">
                  Cuando comienzas a trabajar con nosotros, asignamos a uno de nuestros asesores como tu punto de contacto principal. Así no tienes que explicar tu situación cada vez. Te conocemos, conocemos tu negocio.
                </p>

                <div className="space-y-3">
                  {[
                    { q: "¿Cómo se asigna mi asesor?", a: "Según tu actividad y necesidades: fiscal, contable o seguros. Nos adaptamos a tu perfil desde el primer día." },
                    { q: "¿Puedo cambiar de asesor si lo necesito?", a: "Por supuesto. Solo tienes que comunicarlo y hacemos el traspaso de información de forma ordenada." },
                    { q: "¿Atendéis en valenciano?", a: "Sí, trabajamos en valenciano y castellano sin ningún problema. Somos de aquí." },
                    { q: "¿Tenéis horario de atención presencial?", a: "Lunes a viernes de 9h a 14h. También atendemos con cita previa por las tardes para quienes no pueden venir por la mañana." },
                  ].map((item, i) => (
                    <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setActiveId(activeId === i ? null : i)}
                        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-[#F8F7F4] transition-colors"
                      >
                        <span className="font-semibold text-[#1A1A1A] text-sm">{item.q}</span>
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-3">
                          <i className={`${activeId === i ? "ri-subtract-line" : "ri-add-line"} text-[#6B7F5C] text-base`} />
                        </div>
                      </button>
                      {activeId === i && (
                        <div className="px-5 pb-4">
                          <p className="text-[#6B6B6B] text-sm leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <img
                  src="https://public.readdy.ai/ai/img_res/121183222d01781b2b1072002b0b9492.jpg"
                  alt="Reunión de equipo Vidal Ferrer Asesores"
                  className="w-full h-[480px] md:h-[560px] object-cover object-top rounded-2xl"
                />
                <div className="absolute -bottom-5 -right-5 bg-[#F0F4ED] p-5 rounded-xl text-center">
                  <p className="text-3xl font-bold text-[#6B7F5C]" style={{ fontFamily: "'Playfair Display', serif" }}>+25</p>
                  <p className="text-xs text-[#4A4A4A] font-medium mt-1">años en Pego</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#1A1A1A] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20geometric%20pattern%20dark%20background%20professional%20texture%20subtle%20lines%20corporate%20minimal%20business&width=1440&height=400&seq=cta_equipo1&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-screen-xl mx-auto px-6 md:px-10 text-center">
            <p className="text-[#A0D080] text-sm font-semibold uppercase tracking-widest mb-4">¿Hablamos?</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 max-w-3xl mx-auto leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Pon cara a tu asesor<br />sin ningún compromiso
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto mb-10">
              Primera consulta gratuita. Nos reunimos, analizamos tu caso y te explicamos cómo podemos ayudarte.
            </p>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 bg-[#6B7F5C] hover:bg-[#5A6E4B] text-white font-semibold px-10 py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap text-base"
            >
              Solicitar primera consulta
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
      <ChatWidget />
    </>
  );
}
